import React, { useEffect, useState, useCallback } from 'react';
import { Conversation } from './cvi/components/conversation';
import { useDaily } from '@daily-co/daily-react';

const TavusConversation = ({ conversation, onSendTextMessage, onEnd }) => {
  const [isConnected, setIsConnected] = useState(false);

  
  // Use Daily React hooks
  const daily = useDaily();

  // Listen for Daily.co events
  useEffect(() => {
    if (!daily) return;

    const handleJoinedMeeting = () => {
      console.log('Joined conversation via Daily React hooks');
      console.log('Daily connection state:', {
        meetingState: daily.meetingState(),
        participants: daily.participants(),
        localParticipant: daily.localParticipant,
        conversationId: conversation?.id
      });
      setIsConnected(true);
    };

    const handleLeftMeeting = () => {
      console.log('Left conversation');
      setIsConnected(false);
    };

    const handleError = (error) => {
      console.error('Daily.co error:', error);
      setIsConnected(false);
    };

    const handleAppMessage = (event) => {
      console.log('Received app message:', event);
    };

    // Add event listeners
    daily.on('joined-meeting', handleJoinedMeeting);
    daily.on('left-meeting', handleLeftMeeting);
    daily.on('error', handleError);
    daily.on('app-message', handleAppMessage);

    // Check if already joined
    if (daily.meetingState() === 'joined-meeting') {
      setIsConnected(true);
    }

    // Cleanup
    return () => {
      daily.off('joined-meeting', handleJoinedMeeting);
      daily.off('left-meeting', handleLeftMeeting);
      daily.off('error', handleError);
      daily.off('app-message', handleAppMessage);
    };
  }, [daily]);

  // Function to send text message using Daily React hooks
  const sendTextMessage = useCallback(async (text) => {
    console.log('sendTextMessage called with:', text, typeof text);
    console.trace('Call stack for sendTextMessage:');
    
    if (!isConnected || !daily) {
      console.error('Daily.co not connected:', { isConnected, dailyExists: !!daily, meetingState: daily?.meetingState() });
      return false;
    }

    try {
      console.log(`Sending text message via Daily React hooks: "${text}"`);
      
      // Create the conversation.respond event payload
      const eventPayload = {
        message_type: "conversation",
        event_type: "conversation.respond",
        conversation_id: conversation.id,
        properties: {
          text: text
        }
      };

      console.log('Event payload:', JSON.stringify(eventPayload, null, 2));
      console.log('Daily state before sending:', {
        meetingState: daily.meetingState(),
        participantCount: Object.keys(daily.participants()).length,
        isConnected: isConnected
      });

      // Send using Daily React hooks
      daily.sendAppMessage(eventPayload, '*');
      
      console.log('Text message sent successfully via Daily React hooks');
      return true;

    } catch (error) {
      console.error('Failed to send text message via Daily React hooks:', error);
      return false;
    }
  }, [isConnected, daily, conversation?.id]);

  // Poll for pending messages and send them automatically
  useEffect(() => {
    if (!isConnected || !daily || !conversation) return;

    const pollForMessages = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const endpoint = `${apiUrl}/api/pending-messages/conversation/${conversation.id}`;
        console.log('Polling for messages at:', endpoint);
        
        const response = await fetch(endpoint);
        const data = await response.json();
        
        if (data.success && data.messages.length > 0) {
          console.log(`Found ${data.messages.length} pending messages to send`);
          
          for (const message of data.messages) {
            // Enhanced logging to distinguish message sources
            const messageSource = message.source || 'manual';
            const sourceText = messageSource === 'sms_question' ? 'SMS Question' : 'Manual Input';
            
            console.log(`Auto-sending ${sourceText}: "${message.text}"`);
            
            // Use the same mechanism as the "Send to Avatar" button
            const sent = await sendTextMessage(message.text);
            
            if (sent) {
              // Mark message as sent on backend
              const apiUrl = process.env.REACT_APP_API_URL || '';
              await fetch(`${apiUrl}/api/pending-messages/message/${message.message_id}/sent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
              console.log(`${sourceText} "${message.text}" sent to avatar and marked as completed`);
            }
          }
        }
      } catch (error) {
        console.error('Error polling for pending messages:', error);
      }
    };

    // Poll every 2 seconds for pending messages
    const interval = setInterval(pollForMessages, 2000);
    
    // Also poll immediately
    pollForMessages();
    
    return () => clearInterval(interval);
  }, [isConnected, daily, conversation, sendTextMessage]);

  // Expose the sendTextMessage function to parent component
  useEffect(() => {
    console.log('useEffect triggered - isConnected:', isConnected, 'daily:', !!daily);
    if (onSendTextMessage) {
      if (isConnected && daily) {
        console.log('Setting sendTextMessage function in parent');
        onSendTextMessage(sendTextMessage);
      } else {
        console.log('Setting sendTextMessage to null (not ready)');
        onSendTextMessage(null);
      }
    }
  }, [isConnected, daily, onSendTextMessage]);

  if (!conversation) {
    return (
      <div className="no-conversation">
        <h3>No Active Session</h3>
        <p>Create a new video session to get started</p>
        <div className="placeholder-avatar">
          <div className="avatar-circle">AV</div>
          <p>Your avatar will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tavus-conversation">
      <div className="conversation-header">
        <h3>Active Video Session</h3>
        <div className="conversation-info">
          <span className="conversation-id">ID: {conversation.id}</span>
          <span className={`conversation-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? 'Connected' : 'Connecting...'}
          </span>
        </div>
      </div>
      
      <div className="video-container">
        <Conversation
          conversationUrl={conversation.url}
          onLeave={() => {
            console.log('User left conversation');
            setIsConnected(false);
          }}
          onJoin={() => {
            console.log('User joined conversation');
            setIsConnected(true);
          }}
          onError={(error) => {
            console.error('Conversation error:', error);
            setIsConnected(false);
          }}
          onReady={() => {
            console.log('Conversation ready');
          }}
          style={{ 
            width: '100%', 
            height: '500px',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </div>
      
      <div className="conversation-controls">
        <button 
          onClick={() => window.open(conversation.url, '_blank')}
          className="open-new-tab-btn"
        >
          Open in New Tab
        </button>

        {/*<div className="url-display">
          <small>URL: {conversation.url}</small>
        </div>
        <div className="connection-status">
          <small>
            SMS Status: {isConnected ? 'Ready to Send Messages via Tavus Library' : 'Setting up Tavus connection...'}
          </small>
        </div>*/}
      </div>
    </div>
  );
};

export default TavusConversation;